const PLAY_VALUES = new Set(['true', '1', 'yes', 'on']);

function isProvidedCustomSound(value: unknown): boolean {
  return value !== undefined && value !== null && value !== '';
}

/**
 * FCM sends `data.payload` as a JSON string. Parse it (including a double-encoded
 * string) into an object. Never throw — bad JSON mutes rather than crashing.
 */
export function parseNotificationPayload(
  raw: unknown
): Record<string, unknown> {
  if (!raw) {
    return {};
  }

  if (typeof raw === 'object' && !Array.isArray(raw)) {
    return raw as Record<string, unknown>;
  }

  if (typeof raw !== 'string') {
    return {};
  }

  try {
    let parsed: unknown = JSON.parse(raw);

    // Some gateways double-encode: "\"{...}\"" → string → object
    if (typeof parsed === 'string') {
      parsed = JSON.parse(parsed);
    }

    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>;
    }
  } catch {
    return {};
  }

  return {};
}

/**
 * Reads custom_sound only from the nested `data.payload` JSON.
 * Outer FCM data.custom_sound is ignored. Play only when the nested
 * value is explicitly true (boolean or string); false/undefined/missing mute.
 */
export function shouldPlayCustomSound(
  payloadData: Record<string, unknown> | null | undefined
): boolean {
  const raw = payloadData?.['custom_sound'];

  if (!isProvidedCustomSound(raw)) {
    return false;
  }

  if (typeof raw === 'boolean') {
    return raw;
  }

  if (typeof raw === 'number') {
    return raw !== 0;
  }

  return PLAY_VALUES.has(String(raw).trim().toLowerCase());
}

export function resolveCustomSoundFromPushData(
  data: Record<string, unknown> | null | undefined
): boolean {
  return shouldPlayCustomSound(parseNotificationPayload(data?.['payload']));
}

/** Coerce a resolved SW/page flag (boolean or string) into play vs mute. */
export function isCustomSoundFlagEnabled(value: unknown): boolean {
  return shouldPlayCustomSound({ custom_sound: value });
}

export const NOTIFICATION_SOUND_TYPE = {
  LOGI_CALLING: 'LOGI_CALLING',
} as const;

export type NotificationAudioKey =
  | 'logicalling'
  | 'driver'
  | 'driverHighAudio'
  | 'notification';

/**
 * Reads sound_type only from the nested parsed payload JSON.
 */
export function resolveSoundTypeFromPayload(
  payloadData: Record<string, unknown> | null | undefined
): string | undefined {
  const raw = payloadData?.['sound_type'];
  if (typeof raw !== 'string') {
    return undefined;
  }
  const normalized = raw.trim();
  return normalized || undefined;
}

export function resolveSoundTypeFromPushData(
  data: Record<string, unknown> | null | undefined
): string | undefined {
  return resolveSoundTypeFromPayload(
    parseNotificationPayload(data?.['payload'])
  );
}

/**
 * Select which registered AudioService clip to play when custom_sound is true.
 * LOGI_CALLING overrides ENQUIRY driver sounds.
 */
export function resolveNotificationAudioKey(
  type: unknown,
  soundType: string | undefined,
  isTrackingRoute: boolean
): NotificationAudioKey {
  if (soundType === NOTIFICATION_SOUND_TYPE.LOGI_CALLING) {
    return 'logicalling';
  }

  if (type === 'ENQUIRY') {
    return isTrackingRoute ? 'driver' : 'driverHighAudio';
  }

  return 'notification';
}
